import {
  ComponentRef,
  Injectable,
  Renderer2,
  RendererFactory2,
  SecurityContext,
} from '@angular/core';
import * as L from 'leaflet';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Control, LatLngBounds, Map, PathOptions } from 'leaflet';
import { Indicator } from '../models/indicator';
import { generateHarmoniousColors } from '../utility/color.utility';
import { DataLayerTypeIcon } from '../stores/data-layer-type/data-layer-type.selectors';
import { LegendComponent } from '../components/molecules/legend/legend.component';
import { DataEntry } from '../models/indicator-data';
import { Scale } from '../models/scale';
import { PoiMarkerOptions } from '../models/geo-data';
import { PoiChartData } from './grid-widget-config.service';

interface GestureHandlingMapOptions extends L.MapOptions {
  gestureHandling?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LeafletMapboxService {
  private readonly accessToken =
    'pk.eyJ1IjoiY2hlZ25lciIsImEiOiJjbDN5NWZpcmUwZTkyM2NuNXQ3NGJwOGExIn0.R-Cl0ThFwUS6xI1S61rEtQ';

  private renderer: Renderer2;
  private tileLayerCache: { [key: string]: L.TileLayer } = {};

  private tiles: { [key: string]: string } = {
    //dark: 'assets/vector-layers/dark.json',
    light: 'assets/vector-layers/light.json',
    traffic: 'assets/vector-layers/traffic.json',
    streets: 'assets/vector-layers/streets.json',
    satellite: 'assets/vector-layers/satellite.json',
  };

  constructor(
    private rendererFactory: RendererFactory2,
    private sanitizer: DomSanitizer
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  initializeMap(mapContainer: HTMLElement): Map {
    const mapOptions: GestureHandlingMapOptions = {
      doubleClickZoom: true,
      boxZoom: true,
      touchZoom: true,
      scrollWheelZoom: false,
      gestureHandling: true,
      zoomControl: false, // Disable the default zoom control
      zoomSnap: 0.25,
    };

    return new Map(mapContainer, mapOptions);
  }

  /**
   * Creates and returns a Mapbox GL layer of the specified type.
   *
   * @param {string} type - The type of Mapbox GL layer to create.
   * @return {L.Layer} The created Mapbox GL layer.
   */
  createMapboxGLLayer(type: string): L.Layer {
    if (!this.tileLayerCache[type]) {
      this.initMapboxGLLayer(type);
    }

    return this.tileLayerCache[type];
  }

  /**
   * Initializes a Mapbox GL layer of the specified type and caches it.
   *
   * @param {string} type - The type of the layer to initialize. This corresponds to a key in the `tiles` object.
   * @return {void} This function does not return any value.
   */
  initMapboxGLLayer(type: string): void {
    this.tileLayerCache[type] = (L as any).mapboxGL({
      accessToken: this.accessToken,
      style: this.tiles[type],
      preserveDrawingBuffer: true,
      attribution:
        '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
    });
  }

  /**
   * Remove all layers from the map except the specified one.
   * @param map Leaflet map instance.
   * @param excludeLayer Layer to keep on the map.
   */
  clearLayers(map: L.Map, excludeLayer?: L.Layer): void {
    map.eachLayer(layer => {
      if (excludeLayer && layer === excludeLayer) return;
      map.removeLayer(layer);
    });
  }

  getMapBoxToken(): string {
    return this.accessToken;
  }

  createMarker(
    latlng: L.LatLngExpression,
    svgCode: SafeHtml | undefined,
    classColor: string,
    iconColor: string,
    size = 1
  ): L.Marker {
    const marker = L.marker(latlng);
    let html = '';
    if (svgCode) {
      const svg = this.sanitizer.sanitize(SecurityContext.HTML, svgCode) || '';
      html = `<div class="" style="transform: scale(${size});"><div class="poi-icon" style="background-color: ${classColor}; fill:${iconColor};">${svg}</div></div>`;
    }

    const divIcon = L.divIcon({
      className: '',
      html: html,
      iconSize: [40 * size, 40 * size],
      iconAnchor: [20 * size, 40 * size],
    });

    // Update the marker's icon
    marker.setIcon(divIcon);

    return marker;
  }

  pieChartSvgFromData(chartData: PoiChartData[], chartSize: number): string {
    // Create SVG ring chart
    const total = chartData.reduce((sum, d) => sum + Math.abs(d.value), 0);
    let currentAngle = -90; // Start from top

    const radius = chartSize / 2;
    const innerRadius = radius * 0.55; // 55% inner radius for ring effect
    const centerX = radius;
    const centerY = radius;

    let pathsHTML = '';
    chartData.forEach(segment => {
      const percentage = Math.abs(segment.value) / total;
      const angleSize = percentage * 360;

      const startAngle = currentAngle;
      const endAngle = currentAngle + angleSize;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);

      const x3 = centerX + innerRadius * Math.cos(endRad);
      const y3 = centerY + innerRadius * Math.sin(endRad);
      const x4 = centerX + innerRadius * Math.cos(startRad);
      const y4 = centerY + innerRadius * Math.sin(startRad);

      const largeArc = angleSize > 180 ? 1 : 0;

      pathsHTML += `
            <path d="M ${x1} ${y1}
                     A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
                     L ${x3} ${y3}
                     A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
                     Z"
                  fill="${segment.color}"
                  stroke="white"
                  stroke-width="0"/>
          `;

      currentAngle = endAngle;
    });

    return pathsHTML;
  }

  getSanitizedSvg(svgCode: SafeHtml): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, svgCode) || '';
  }

  getBoundsFromObject(bounds: any): LatLngBounds {
    const sw = bounds._southWest;
    const ne = bounds._northEast;
    return L.latLngBounds(L.latLng(sw.lat, sw.lng), L.latLng(ne.lat, ne.lng));
  }
}
